import { useState, useRef, useEffect, FC } from 'react'
import { EmotionListConfig } from '../tts/config'
import { useInputSanitize } from '../../hooks/useInputSanitize'
import { CustomIcon } from '../../components/customIcon/customIcon'
import { PortalTooltip } from '../../components/tooltip/portalTooltip'
import classes from '../../styles/voices/emotionSelect.module.scss'

type TEmotionItem = (typeof EmotionListConfig)[number]

interface EmotionSelectProps {
  value: TEmotionItem[]
  onChange: (emotions: TEmotionItem[], names: Record<string, string>, defaultEmotion: string | null) => void
  resetKey?: number
  allowedEmotions?: string[] | null
  initialDefaultEmotion?: string | null
}

export const EmotionSelect: FC<EmotionSelectProps> = ({ value, onChange, resetKey, allowedEmotions, initialDefaultEmotion }) => {
  const filteredEmotions = allowedEmotions?.length ? EmotionListConfig.filter((e) => allowedEmotions.includes(e.name)) : []
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<TEmotionItem | null>(value[0] ?? null)
  const [hoveredEmotion, setHoveredEmotion] = useState<string | null>(null)
  const [defaultEmotion, setDefaultEmotion] = useState<string | null>(initialDefaultEmotion ?? null)
  const { value: editName, setValue: setEditName, onChange: onEditNameChange, onBlur: onEditNameBlur, reset: resetEditName } = useInputSanitize('', { maxLength: 50 })
  const [emotionNames, setEmotionNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(filteredEmotions.map((e) => [e.name, e.tittle ?? e.name]))
  )
  const [savedEmotions, setSavedEmotions] = useState<Set<string>>(
    value.length > 0 ? new Set(value.map((e) => e.name)) : filteredEmotions.length > 0 ? new Set(filteredEmotions.map((e) => e.name)) : new Set()
  )

  useEffect(() => {
    if (resetKey === undefined) return
    const names = Object.fromEntries(filteredEmotions.map((e) => [e.name, e.tittle ?? e.name]))
    setSelected(value[0] ?? null)
    const initialSet = value.length > 0
      ? new Set(value.map((e) => e.name))
      : filteredEmotions.length > 0 ? new Set(filteredEmotions.map((e) => e.name)) : new Set<string>()
    setSavedEmotions(initialSet)
    setEmotionNames(names)
    setDefaultEmotion(initialDefaultEmotion ?? null)
    onChange(filteredEmotions.filter((e) => initialSet.has(e.name)), names, initialDefaultEmotion ?? null)
    resetEditName()
  }, [resetKey])

  useEffect(() => {
    setEmotionNames(Object.fromEntries(filteredEmotions.map((e) => [e.name, e.tittle ?? e.name])))
  }, [allowedEmotions])
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (selected) setEditName(emotionNames[selected.name] ?? '')
  }, [selected])

  const handleSelect = (emotion: TEmotionItem) => {
    setSelected(emotion)
    setEditName(emotionNames[emotion.name] ?? '')
    setSavedEmotions((prev) => {
      const next = new Set(prev)
      if (next.has(emotion.name)) {
        if (next.size === 1) return prev
        if (defaultEmotion === emotion.name) return prev
        next.delete(emotion.name)
        onChange(filteredEmotions.filter((e) => next.has(e.name)), emotionNames, defaultEmotion)
      } else {
        next.add(emotion.name)
        onChange(filteredEmotions.filter((e) => next.has(e.name)), emotionNames, defaultEmotion)
      }
      return next
    })
  }

  const handleSetDefault = (e: React.MouseEvent, emotionName: string) => {
    e.stopPropagation()
    setSavedEmotions((prev) => {
      const next = new Set(prev).add(emotionName)
      setDefaultEmotion(emotionName)
      onChange(filteredEmotions.filter((em) => next.has(em.name)), emotionNames, emotionName)
      return next
    })
  }

  const handleRename = () => {
    if (!selected || !editName.trim()) return
    const newNames = { ...emotionNames, [selected.name]: editName.trim() }
    setEmotionNames(newNames)
    setSavedEmotions((prev) => {
      const next = new Set(prev).add(selected.name)
      onChange(filteredEmotions.filter((e) => next.has(e.name)), newNames, defaultEmotion)
      return next
    })
  }

  const isDisabled = !allowedEmotions || allowedEmotions.length === 0

  return (
    <div className={classes.wrapper} ref={wrapperRef}>
      <PortalTooltip
        placement="bottom-middle"
        tooltipContent="У этого голоса нет доступных эмоций"
        hideTooltip={!isDisabled}
      >
      <button
        type="button"
        className={classes.trigger}
        onClick={() => !isDisabled && setOpen((o) => !o)}
        disabled={isDisabled}
      >
        {savedEmotions.size > 0 ? (
          <span className={classes.triggerEmojis}>
            {filteredEmotions.filter((e) => savedEmotions.has(e.name)).map((e, i, arr) => (
              <span key={e.name}>
                {e.url
                  ? <img src={e.url} alt={emotionNames[e.name]} className={classes.triggerEmoji} />
                  : <span className={classes.triggerEmoji}>{e.emoji}</span>
                }
                {i < arr.length - 1 && <span className={classes.triggerSep}>,</span>}
              </span>
            ))}
          </span>
        ) : (
          <span className={classes.triggerPlaceholder}>Эмоция</span>
        )}
        <span className={classes.triggerChevron}>
          <CustomIcon icon={open ? 'arrowDown' : 'arrowRight'} />
        </span>
      </button>
      </PortalTooltip>

      {open && (
        <div className={classes.dropdown}>
          <div className={classes.grid}>
            {filteredEmotions.map((emotion) => {
              const isEditing = selected?.name === emotion.name
              const isSaved = savedEmotions.has(emotion.name)
              const isDefault = defaultEmotion === emotion.name
              const isHovered = hoveredEmotion === emotion.name
              return (
                <button
                  key={emotion.name}
                  type="button"
                  className={`${classes.emotionItem} ${isEditing ? classes.emotionItemActive : ''}`}
                  onClick={() => handleSelect(emotion)}
                >
                  {emotion.url
                    ? <img src={emotion.url} alt={emotionNames[emotion.name]} className={classes.emotionEmoji} />
                    : <span className={classes.emotionEmojiText}>{emotion.emoji}</span>
                  }
                  <span className={classes.emotionName}>{emotionNames[emotion.name]}</span>
                  {isSaved && (
                    isDefault ? (
                      <span
                        className={classes.emotionCrown}
                        onMouseEnter={() => setHoveredEmotion(emotion.name)}
                        onMouseLeave={() => setHoveredEmotion(null)}
                      >
                        <CustomIcon icon="crown" />
                      </span>
                    ) : (
                      <span
                        className={classes.emotionCrown}
                        onMouseEnter={() => setHoveredEmotion(emotion.name)}
                        onMouseLeave={() => setHoveredEmotion(null)}
                        onClick={(e) => { e.stopPropagation(); if (isHovered) handleSetDefault(e, emotion.name) }}
                      >
                        {isHovered
                          ? <CustomIcon icon="crown" />
                          : (
                            <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
                              <path d="M3 8l3.5 3.5L13 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )
                        }
                      </span>
                    )
                  )}
                  {!isSaved && (
                    <span
                      className={classes.emotionCrownHover}
                      onMouseEnter={() => setHoveredEmotion(emotion.name)}
                      onMouseLeave={() => setHoveredEmotion(null)}
                      onClick={(e) => { e.stopPropagation(); handleSetDefault(e, emotion.name) }}
                    >
                      {isHovered && <CustomIcon icon="crown" />}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          {selected && (
            <div className={classes.renameBar}>
              <div className={classes.renameEmoji}>
                {selected.url
                  ? <img src={selected.url} alt={emotionNames[selected.name]} className={classes.renameEmojiImg} />
                  : <span className={classes.emotionEmojiText}>{selected.emoji}</span>
                }
              </div>
              <div className={classes.renameInputWrap}>
                <input
                  className={classes.renameInput}
                  value={editName}
                  onChange={onEditNameChange}
                  onBlur={onEditNameBlur}
                  onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                />
              </div>
              <button type="button" className={classes.renameBtn} onClick={handleRename}>
                Заменить
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
